import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import type { VideoExpressStyleRecord } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Clapperboard, Plus, Pencil, Archive, Loader2, Search, Image as ImageIcon, Upload } from "lucide-react";

// ---- form state ----------------------------------------------------------

type FormState = {
  value: string;
  label: string;
  description: string;
  styleDescription: string;
  visualStyle: string;
  scriptStyle: string;
  videoPromptSuffix: string;
  maleVoiceId: string;
  femaleVoiceId: string;
  imageUrl: string;
};

const EMPTY_FORM: FormState = {
  value: "", label: "", description: "", styleDescription: "", visualStyle: "",
  scriptStyle: "", videoPromptSuffix: "", maleVoiceId: "", femaleVoiceId: "", imageUrl: "",
};

function styleToForm(s: VideoExpressStyleRecord): FormState {
  return {
    value: s.value ?? "",
    label: s.label ?? "",
    description: s.description ?? "",
    styleDescription: s.styleDescription ?? "",
    visualStyle: s.visualStyle ?? "",
    scriptStyle: s.scriptStyle ?? "",
    videoPromptSuffix: s.videoPromptSuffix ?? "",
    maleVoiceId: s.maleVoiceId ?? "",
    femaleVoiceId: s.femaleVoiceId ?? "",
    imageUrl: s.imageUrl ?? "",
  };
}

function formToPayload(f: FormState): Record<string, unknown> {
  const slug = (f.value.trim() || f.label.trim())
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .replace(/^_+|_+$/g, "");
  return {
    value: slug,
    label: f.label.trim(),
    description: f.description.trim() || null,
    styleDescription: f.styleDescription.trim() || null,
    visualStyle: f.visualStyle.trim() || null,
    scriptStyle: f.scriptStyle.trim() || null,
    videoPromptSuffix: f.videoPromptSuffix.trim() || null,
    maleVoiceId: f.maleVoiceId.trim() || null,
    femaleVoiceId: f.femaleVoiceId.trim() || null,
    imageUrl: f.imageUrl.trim() || null,
  };
}

// ---- field components -----------------------------------------------------

function TextField({ id, label, value, onChange, placeholder, mono }: {
  id: string; label: string; value: string; onChange: (v: string) => void; placeholder?: string; mono?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} placeholder={placeholder} className={mono ? "font-mono text-xs" : undefined}
        onChange={(e) => onChange(e.target.value)} data-testid={`input-${id}`} />
    </div>
  );
}

function AreaField({ id, label, value, onChange, hint, rows = 3 }: {
  id: string; label: string; value: string; onChange: (v: string) => void; hint?: string; rows?: number;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <Textarea id={id} value={value} rows={rows} className="font-normal"
        onChange={(e) => onChange(e.target.value)} data-testid={`textarea-${id}`} />
    </div>
  );
}

// ---- manager ---------------------------------------------------------------
// Embeddable Video Styles management surface. Rendered inside the Admin
// dashboard (Story & Video Styles → Video Styles) — no page-level layout wrapper.

export default function VideoExpressStylesManager() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<VideoExpressStyleRecord | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [archiveTarget, setArchiveTarget] = useState<VideoExpressStyleRecord | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isAdmin = !!user?.isMaster || !!user?.isSuperuser;

  // Global query defaults (retry:false + staleTime:Infinity) would cache a
  // transient first-paint 401 forever and wedge this shared endpoint empty, so
  // override them: retry the auth race and re-fetch on mount.
  const { data: styles, isLoading } = useQuery<VideoExpressStyleRecord[]>({
    queryKey: ["/api/video-express-styles"],
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    staleTime: 30_000,
    refetchOnMount: true,
  });

  const upd = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

  const handleImageUpload = async (file: File) => {
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/video-express-styles/upload-image", {
        method: "POST", body: fd, credentials: "include",
      });
      if (!res.ok) {
        const msg = await res.json().catch(() => ({ message: "Upload failed" }));
        throw new Error(msg.message || "Upload failed");
      }
      const data = await res.json();
      upd({ imageUrl: data.url });
      toast({ title: "Photo attached" });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploadingImage(false);
    }
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = formToPayload(form);
      if (!payload.label) throw new Error("Label is required");
      if (!payload.value) throw new Error("Slug is required");
      if (editing) {
        return apiRequest("PATCH", `/api/video-express-styles/${editing.id}`, payload);
      }
      return apiRequest("POST", "/api/video-express-styles", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/video-express-styles"] });
      setDialogOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      toast({ title: editing ? "Video style updated" : "Video style created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const archiveMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/video-express-styles/${id}/archive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/video-express-styles"] });
      setArchiveTarget(null);
      toast({ title: "Video style deleted" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };
  const openEdit = (s: VideoExpressStyleRecord) => {
    setEditing(s);
    setForm(styleToForm(s));
    setDialogOpen(true);
  };

  if (!isAdmin) {
    return (
      <div className="p-8 max-w-md mx-auto text-center space-y-2">
        <Clapperboard className="h-10 w-10 mx-auto text-muted-foreground" />
        <h1 className="text-lg font-semibold">Video Styles</h1>
        <p className="text-sm text-muted-foreground">This library is available to master and superuser accounts only.</p>
      </div>
    );
  }

  const filtered = (styles ?? []).filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.label.toLowerCase().includes(q) ||
      (s.value ?? "").toLowerCase().includes(q) ||
      (s.description ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Clapperboard className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold" data-testid="text-page-title">Video Styles</h1>
          </div>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Global library of Video Express Max visual styles. Each style drives how the video
            looks, how the voiceover sounds, and the per-scene motion direction. Changes appear
            instantly in the create-page style picker.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="button-new-style">
          <Plus className="h-4 w-4 mr-2" /> New style
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search styles..." className="pl-9" data-testid="input-search" />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground" data-testid="text-empty">
          No video styles yet. Create one to get started.
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((s) => (
            <Card key={s.id} data-testid={`card-style-${s.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    {s.imageUrl ? (
                      <img src={s.imageUrl} alt="" className="h-12 w-12 rounded-lg object-cover border border-border/50 shrink-0" data-testid={`img-style-${s.id}`} />
                    ) : (
                      <div className="h-12 w-12 rounded-lg bg-muted/50 border border-border/40 flex items-center justify-center shrink-0">
                        <ImageIcon className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="space-y-1 min-w-0">
                      <CardTitle className="text-base" data-testid={`text-style-label-${s.id}`}>{s.label}</CardTitle>
                      <p className="text-xs text-muted-foreground font-mono">{s.value}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(s)}
                      data-testid={`button-edit-${s.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setArchiveTarget(s)}
                      data-testid={`button-delete-${s.id}`}>
                      <Archive className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {s.description && <p className="text-muted-foreground">{s.description}</p>}
                <div className="grid gap-2 sm:grid-cols-2">
                  {(s.styleDescription || s.visualStyle) && (
                    <Detail label="Visual style" text={`${s.styleDescription ?? ""} ${s.visualStyle ?? ""}`.trim()} />
                  )}
                  {s.scriptStyle && <Detail label="Voiceover style" text={s.scriptStyle} />}
                  {s.videoPromptSuffix && <Detail label="Video prompt suffix" text={s.videoPromptSuffix} />}
                  <Detail label="Voice IDs"
                    text={`Male: ${s.maleVoiceId || "—"}  ·  Female: ${s.femaleVoiceId || "—"}`} mono />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit video style" : "New video style"}</DialogTitle>
            <DialogDescription>
              These fields drive how the video looks, sounds, and moves. The slug is the stable
              value campaigns save — changing it won't affect campaigns that already chose it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <TextField id="label" label="Label" value={form.label} onChange={(v) => upd({ label: v })}
                placeholder="Claymation" />
              <TextField id="value" label="Slug (value)" value={form.value} onChange={(v) => upd({ value: v })}
                placeholder="auto from label if blank" mono />
            </div>
            <div className="space-y-1.5">
              <Label>Picker photo (500×500)</Label>
              <p className="text-xs text-muted-foreground">Shown on this style's card in the create-page picker. Any image works — it's cropped to a centered 500×500 square.</p>
              <div className="flex items-center gap-3">
                <div className="h-20 w-20 rounded-lg border border-border/60 bg-muted/40 overflow-hidden shrink-0 flex items-center justify-center">
                  {form.imageUrl ? (
                    <img src={form.imageUrl} alt="" className="h-full w-full object-cover" data-testid="img-photo-preview" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col items-start gap-1.5">
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageUpload(f); e.target.value = ""; }}
                    data-testid="input-photo" />
                  <Button type="button" variant="outline" size="sm" disabled={uploadingImage}
                    onClick={() => fileInputRef.current?.click()} data-testid="button-upload-photo">
                    {uploadingImage ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                    {form.imageUrl ? "Replace photo" : "Upload photo"}
                  </Button>
                  {form.imageUrl && (
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-muted-foreground"
                      onClick={() => upd({ imageUrl: "" })} data-testid="button-remove-photo">Remove</Button>
                  )}
                </div>
              </div>
            </div>
            <TextField id="description" label="Short description / subtitle" value={form.description}
              onChange={(v) => upd({ description: v })} placeholder="Handcrafted clay, stop-motion" />
            <AreaField id="styleDescription" label="Visual-style brief (opening line)" value={form.styleDescription}
              onChange={(v) => upd({ styleDescription: v })} rows={2}
              hint="The one-line creative brief, e.g. 'Make a handcrafted claymation commercial.'" />
            <AreaField id="visualStyle" label="Visual style (how it should look)" value={form.visualStyle}
              onChange={(v) => upd({ visualStyle: v })} rows={3}
              hint="Appended to the brief above to form the full visual direction." />
            <AreaField id="scriptStyle" label="Voiceover style" value={form.scriptStyle}
              onChange={(v) => upd({ scriptStyle: v })} rows={2}
              hint="How the narration should sound, e.g. 'Playful, conversational, and energetic.'" />
            <AreaField id="videoPromptSuffix" label="Per-scene video prompt suffix" value={form.videoPromptSuffix}
              onChange={(v) => upd({ videoPromptSuffix: v })} rows={3}
              hint="Appended verbatim to every generated per-scene video prompt." />
            <div className="grid grid-cols-2 gap-3">
              <TextField id="maleVoiceId" label="Male voice ID" value={form.maleVoiceId}
                onChange={(v) => upd({ maleVoiceId: v })} mono placeholder="ElevenLabs voice ID" />
              <TextField id="femaleVoiceId" label="Female voice ID" value={form.femaleVoiceId}
                onChange={(v) => upd({ femaleVoiceId: v })} mono placeholder="ElevenLabs voice ID" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="button-cancel">Cancel</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} data-testid="button-save">
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete video style?</AlertDialogTitle>
            <AlertDialogDescription>
              "{archiveTarget?.label}" will be removed from the create-page picker. Campaigns that
              already chose it keep working — they still resolve to coherent guidance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => archiveTarget && archiveMutation.mutate(archiveTarget.id)}
              data-testid="button-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Detail({ label, text, mono }: { label: string; text: string; mono?: boolean }) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={mono ? "text-xs font-mono break-all" : "text-sm"}>{text}</p>
    </div>
  );
}
